import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  addDoc,
  collection,
  doc,
  getDocs,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import { db } from "./firebase";
import { sendUserNotification } from "./notificationApi";
import { EmptyState, PageHeader, StatCard, ToneBadge } from "./petaarUi";

const TICKET_CATEGORIES = [
  "Booking issue",
  "Payment issue",
  "Account access",
  "Service quality",
  "Refund request",
  "Technical problem",
  "Other",
];
const PRIORITY_OPTIONS = ["low", "medium", "high", "urgent"];
const STATUS_OPTIONS = ["open", "in_progress", "resolved", "closed"];

const toMillis = (value) => {
  if (!value) return 0;
  if (value.seconds) return value.seconds * 1000;
  const parsed = new Date(value).getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
};

const sortTickets = (rows) =>
  [...rows].sort(
    (a, b) =>
      toMillis(b.lastMessageAt || b.updatedAt || b.createdAt) -
      toMillis(a.lastMessageAt || a.updatedAt || a.createdAt),
  );

const roleText = (role) => {
  if (role === "admin") return "Admin";
  if (role === "provider") return "Service Provider";
  return "Pet Owner";
};

export default function SupportTickets({ user, role }) {
  const isAdmin = role === "admin";
  const [tickets, setTickets] = useState([]);
  const [messages, setMessages] = useState([]);
  const [selectedTicketId, setSelectedTicketId] = useState("");
  const [loadingTickets, setLoadingTickets] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [creatingTicket, setCreatingTicket] = useState(false);
  const [sendingReply, setSendingReply] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [replyText, setReplyText] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [priorityFilter, setPriorityFilter] = useState("");
  const [searchFilter, setSearchFilter] = useState("");
  const [quickUpdatingId, setQuickUpdatingId] = useState("");
  const [draft, setDraft] = useState({
    subject: "",
    category: TICKET_CATEGORIES[0],
    priority: "medium",
    bookingId: "",
    description: "",
  });

  const loadTickets = useCallback(async () => {
    if (!user?.uid) {
      setTickets([]);
      setSelectedTicketId("");
      return;
    }

    setLoadingTickets(true);
    try {
      setLoadError("");
      let snap;
      if (isAdmin) {
        snap = await getDocs(collection(db, "supportTickets"));
      } else {
        snap = await getDocs(query(collection(db, "supportTickets"), where("createdByUid", "==", user.uid)));
      }
      const rows = snap.docs.map((item) => ({ id: item.id, ...item.data() }));
      setTickets(sortTickets(rows));
    } catch (error) {
      setLoadError(error.message || "Unable to load tickets.");
      setTickets([]);
      setSelectedTicketId("");
    } finally {
      setLoadingTickets(false);
    }
  }, [isAdmin, user?.uid]);

  const loadMessages = useCallback(async (ticketId) => {
    if (!ticketId) {
      setMessages([]);
      return;
    }
    setLoadingMessages(true);
    try {
      setLoadError("");
      const snap = await getDocs(collection(db, "supportTickets", ticketId, "messages"));
      const rows = snap.docs.map((item) => ({ id: item.id, ...item.data() }));
      rows.sort((a, b) => toMillis(a.createdAt) - toMillis(b.createdAt));
      setMessages(rows);
    } catch (error) {
      setLoadError(error.message || "Unable to load messages.");
      setMessages([]);
    } finally {
      setLoadingMessages(false);
    }
  }, []);

  useEffect(() => {
    loadTickets();
  }, [loadTickets]);

  const filteredTickets = useMemo(() => {
    return tickets
      .filter((ticket) => (statusFilter ? ticket.status === statusFilter : true))
      .filter((ticket) => (roleFilter ? ticket.createdByRole === roleFilter : true))
      .filter((ticket) => (priorityFilter ? ticket.priority === priorityFilter : true))
      .filter((ticket) => {
        const q = searchFilter.trim().toLowerCase();
        if (!q) return true;
        return (
          String(ticket.subject || "").toLowerCase().includes(q) ||
          String(ticket.category || "").toLowerCase().includes(q) ||
          String(ticket.createdByEmail || "").toLowerCase().includes(q) ||
          String(ticket.bookingId || "").toLowerCase().includes(q)
        );
      });
  }, [tickets, statusFilter, roleFilter, priorityFilter, searchFilter]);

  const ticketStats = useMemo(() => {
    const counts = {
      open: 0,
      in_progress: 0,
      resolved: 0,
      closed: 0,
    };
    filteredTickets.forEach((ticket) => {
      const key = String(ticket.status || "open").toLowerCase();
      if (counts[key] === undefined) {
        counts.open += 1;
      } else {
        counts[key] += 1;
      }
    });
    return {
      total: filteredTickets.length,
      ...counts,
    };
  }, [filteredTickets]);

  useEffect(() => {
    if (filteredTickets.length === 0) {
      setSelectedTicketId("");
      return;
    }
    const present = filteredTickets.some((ticket) => ticket.id === selectedTicketId);
    if (!present) {
      setSelectedTicketId(filteredTickets[0].id);
    }
  }, [filteredTickets, selectedTicketId]);

  useEffect(() => {
    if (!selectedTicketId) {
      setMessages([]);
      return;
    }
    loadMessages(selectedTicketId);
  }, [loadMessages, selectedTicketId]);

  const selectedTicket = useMemo(
    () => tickets.find((ticket) => ticket.id === selectedTicketId) || null,
    [tickets, selectedTicketId],
  );

  const addOwnerNotification = async (ticket, title, message) => {
    if (!ticket?.createdByEmail || !isAdmin || !user?.email) return;
    try {
      await sendUserNotification({
        recipientEmail: ticket.createdByEmail,
        type: "support_ticket",
        title,
        message,
        ticketId: ticket.id,
      });
    } catch (error) {
      console.error("Notification create failed", error);
    }
  };

  const createTicket = async () => {
    const subject = String(draft.subject || "").trim();
    const description = String(draft.description || "").trim();
    if (!subject || !description) {
      alert("Subject and issue details are required.");
      return;
    }
    if (!user?.uid) return;

    setCreatingTicket(true);
    try {
      const ticketRef = await addDoc(collection(db, "supportTickets"), {
        createdByUid: user.uid,
        createdByEmail: user.email || "",
        createdByRole: role || "owner",
        subject,
        category: draft.category || "Other",
        priority: draft.priority || "medium",
        status: "open",
        bookingId: String(draft.bookingId || "").trim(),
        description,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        lastMessageAt: serverTimestamp(),
      });
      await addDoc(collection(ticketRef, "messages"), {
        senderUid: user.uid,
        senderEmail: user.email || "",
        senderRole: role || "owner",
        text: description,
        createdAt: serverTimestamp(),
      });
      setDraft({
        subject: "",
        category: TICKET_CATEGORIES[0],
        priority: "medium",
        bookingId: "",
        description: "",
      });
      await loadTickets();
      setSelectedTicketId(ticketRef.id);
      setReplyText("");
      alert("Support ticket created.");
    } catch (error) {
      alert(error.message || "Unable to create ticket.");
    } finally {
      setCreatingTicket(false);
    }
  };

  const sendReply = async () => {
    const text = String(replyText || "").trim();
    if (!selectedTicket || !text || !user?.uid) return;

    setSendingReply(true);
    try {
      await addDoc(collection(db, "supportTickets", selectedTicket.id, "messages"), {
        senderUid: user.uid,
        senderEmail: user.email || "",
        senderRole: role || "owner",
        text,
        createdAt: serverTimestamp(),
      });
      await updateDoc(doc(db, "supportTickets", selectedTicket.id), {
        updatedAt: serverTimestamp(),
        lastMessageAt: serverTimestamp(),
      });

      if (isAdmin) {
        await addOwnerNotification(
          selectedTicket,
          `Reply on ticket ${selectedTicket.subject || selectedTicket.id}`,
          "Petaar Support has replied to your ticket.",
        );
      }

      setReplyText("");
      await Promise.all([loadMessages(selectedTicket.id), loadTickets()]);
    } catch (error) {
      alert(error.message || "Unable to send reply.");
    } finally {
      setSendingReply(false);
    }
  };

  const changeStatus = async (nextStatus, ticketOverride = null) => {
    const targetTicket = ticketOverride || selectedTicket;
    if (!targetTicket) return;
    setUpdatingStatus(true);
    try {
      const payload = {
        status: nextStatus,
        updatedAt: serverTimestamp(),
        lastMessageAt: serverTimestamp(),
      };
      if (nextStatus === "closed") payload.closedAt = serverTimestamp();
      if (nextStatus === "resolved") payload.resolvedAt = serverTimestamp();
      await updateDoc(doc(db, "supportTickets", targetTicket.id), payload);

      if (isAdmin) {
        await addOwnerNotification(
          targetTicket,
          `Ticket status updated: ${nextStatus}`,
          `Your ticket "${targetTicket.subject || targetTicket.id}" is now ${nextStatus}.`,
        );
      }

      await loadTickets();
    } catch (error) {
      alert(error.message || "Unable to update ticket status.");
    } finally {
      setUpdatingStatus(false);
    }
  };

  const quickSetStatus = async (ticket, nextStatus) => {
    if (!ticket || !nextStatus) return;
    setQuickUpdatingId(`${ticket.id}:${nextStatus}`);
    try {
      await changeStatus(nextStatus, ticket);
    } finally {
      setQuickUpdatingId("");
    }
  };

  const exportTicketsCsv = () => {
    if (!filteredTickets.length) {
      alert("No tickets to export.");
      return;
    }
    const headers = [
      "ticket_id",
      "subject",
      "category",
      "priority",
      "status",
      "created_by_role",
      "created_by_email",
      "booking_id",
      "created_at",
      "updated_at",
      "last_message_at",
    ];
    const escapeCsv = (value) => {
      const text = String(value ?? "");
      if (/[",\n]/.test(text)) {
        return `"${text.replace(/"/g, "\"\"")}"`;
      }
      return text;
    };
    const rows = filteredTickets.map((ticket) => ([
      ticket.id || "",
      ticket.subject || "",
      ticket.category || "",
      ticket.priority || "",
      ticket.status || "",
      ticket.createdByRole || "",
      ticket.createdByEmail || "",
      ticket.bookingId || "",
      toMillis(ticket.createdAt) ? new Date(toMillis(ticket.createdAt)).toISOString() : "",
      toMillis(ticket.updatedAt) ? new Date(toMillis(ticket.updatedAt)).toISOString() : "",
      toMillis(ticket.lastMessageAt) ? new Date(toMillis(ticket.lastMessageAt)).toISOString() : "",
    ].map(escapeCsv).join(",")));
    const csv = [headers.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `support-tickets-${Date.now()}.csv`;
    anchor.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="petaar-page">
      <PageHeader
        eyebrow={isAdmin ? "Support Desk" : "Support"}
        title={isAdmin ? "Support operations" : "Support Tickets"}
        subtitle={isAdmin ?
          "Manage all platform support requests from pet owners and service providers." :
          "Raise an issue and track replies from Petaar Support in one place."}
        actions={(
          <>
            <button className="petaar-button--secondary" onClick={exportTicketsCsv}>Export CSV</button>
            <button className="petaar-button" onClick={loadTickets} disabled={loadingTickets}>
              {loadingTickets ? "Refreshing..." : "Refresh"}
            </button>
          </>
        )}
      />

      {!isAdmin && (
        <div className="petaar-card" style={{ marginBottom: 16 }}>
          <h3 style={{ marginTop: 0 }}>Create New Ticket</h3>
          <div className="petaar-form-grid">
            <input
              value={draft.subject}
              onChange={(e) => setDraft((prev) => ({ ...prev, subject: e.target.value }))}
              placeholder="Subject"
            />
            <select
              value={draft.category}
              onChange={(e) => setDraft((prev) => ({ ...prev, category: e.target.value }))}
            >
              {TICKET_CATEGORIES.map((item) => (
                <option key={item} value={item}>{item}</option>
              ))}
            </select>
            <select
              value={draft.priority}
              onChange={(e) => setDraft((prev) => ({ ...prev, priority: e.target.value }))}
            >
              {PRIORITY_OPTIONS.map((item) => (
                <option key={item} value={item}>{item}</option>
              ))}
            </select>
            <input
              value={draft.bookingId}
              onChange={(e) => setDraft((prev) => ({ ...prev, bookingId: e.target.value }))}
              placeholder="Booking ID (optional)"
            />
          </div>
          <textarea
            value={draft.description}
            onChange={(e) => setDraft((prev) => ({ ...prev, description: e.target.value }))}
            placeholder="Describe your issue in detail"
            style={{ width: "100%", marginTop: 12 }}
          />
          <button className="petaar-button" onClick={createTicket} disabled={creatingTicket} style={{ marginTop: 12 }}>
            {creatingTicket ? "Creating..." : "Submit Ticket"}
          </button>
        </div>
      )}

      {loadError && (
        <div className="petaar-notice error" style={{ marginBottom: 16 }}>
          {loadError}
        </div>
      )}

      <div className="petaar-card" style={{ marginBottom: 18 }}>
        <div className="petaar-form-grid">
        <input
          value={searchFilter}
          onChange={(e) => setSearchFilter(e.target.value)}
          placeholder="Search ticket subject, category, booking ID"
        />
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="">All Status</option>
          {STATUS_OPTIONS.map((status) => (
            <option key={status} value={status}>{status}</option>
          ))}
        </select>
        {isAdmin && (
          <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}>
            <option value="">All User Roles</option>
            <option value="owner">Pet Owner</option>
            <option value="provider">Service Provider</option>
            <option value="admin">Admin</option>
          </select>
        )}
        <select value={priorityFilter} onChange={(e) => setPriorityFilter(e.target.value)}>
          <option value="">All Priority</option>
          {PRIORITY_OPTIONS.map((priority) => (
            <option key={priority} value={priority}>{priority}</option>
          ))}
        </select>
        <button
          className="petaar-button--secondary"
          onClick={() => {
            setSearchFilter("");
            setStatusFilter("");
            setRoleFilter("");
            setPriorityFilter("");
          }}
        >
          Clear Filters
        </button>
        </div>
      </div>

      <div className="petaar-stat-grid" style={{ marginBottom: 20 }}>
        <StatCard icon="🎫" label="Total" value={ticketStats.total} />
        <StatCard icon="🟠" label="Open" value={ticketStats.open} tone="warning" />
        <StatCard icon="🔵" label="In Progress" value={ticketStats.in_progress} tone="default" />
        <StatCard icon="🟢" label="Resolved" value={ticketStats.resolved} tone="success" />
      </div>

      <div className="petaar-sidebar-layout" style={{ gridTemplateColumns: "minmax(290px, 360px) minmax(0, 1fr)" }}>
        <div className="petaar-card" style={{ maxHeight: 640, overflowY: "auto" }}>
          {loadingTickets ? (
            <p style={{ padding: 12 }}>Loading tickets...</p>
          ) : filteredTickets.length === 0 ? (
            <EmptyState
              icon="📮"
              title="No tickets found"
              description="Try a broader search or create a new ticket."
            />
          ) : (
            filteredTickets.map((ticket) => {
              const active = ticket.id === selectedTicketId;
              return (
                <div
                  key={ticket.id}
                  style={{
                    padding: 12,
                    borderBottom: "1px solid #e2e8f0",
                    background: active ? "#f8fbff" : "#fff",
                  }}
                >
                  <button
                    onClick={() => setSelectedTicketId(ticket.id)}
                    style={{
                      display: "block",
                      textAlign: "left",
                      width: "100%",
                      border: "none",
                      background: "transparent",
                      padding: 0,
                      cursor: "pointer",
                    }}
                  >
                    <strong>{ticket.subject || "Untitled ticket"}</strong>
                    <div style={{ marginTop: 6, display: "flex", gap: 6, flexWrap: "wrap" }}>
                      <ToneBadge tone={ticket.status === "resolved" ? "success" : ticket.status === "closed" ? "default" : "warning"}>
                        {ticket.status || "open"}
                      </ToneBadge>
                      <ToneBadge tone={ticket.priority === "urgent" || ticket.priority === "high" ? "danger" : ticket.priority === "medium" ? "warning" : "default"}>
                        {ticket.priority || "medium"}
                      </ToneBadge>
                    </div>
                    <small style={{ display: "block", marginTop: 6, color: "#64748b" }}>
                      {ticket.category || "General"} | {roleText(ticket.createdByRole)}
                    </small>
                    <small style={{ display: "block", color: "#64748b" }}>
                      {toMillis(ticket.lastMessageAt || ticket.updatedAt || ticket.createdAt) ?
                        new Date(toMillis(ticket.lastMessageAt || ticket.updatedAt || ticket.createdAt)).toLocaleString() :
                        "Just now"}
                    </small>
                  </button>
                  {isAdmin && (
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8 }}>
                      {ticket.status !== "in_progress" && (
                        <button
                          onClick={() => quickSetStatus(ticket, "in_progress")}
                          disabled={Boolean(quickUpdatingId)}
                        >
                          {quickUpdatingId === `${ticket.id}:in_progress` ? "Updating..." : "Take"}
                        </button>
                      )}
                      {ticket.status !== "resolved" && (
                        <button
                          onClick={() => quickSetStatus(ticket, "resolved")}
                          disabled={Boolean(quickUpdatingId)}
                        >
                          {quickUpdatingId === `${ticket.id}:resolved` ? "Updating..." : "Resolve"}
                        </button>
                      )}
                      {ticket.status !== "closed" && (
                        <button
                          onClick={() => quickSetStatus(ticket, "closed")}
                          disabled={Boolean(quickUpdatingId)}
                        >
                          {quickUpdatingId === `${ticket.id}:closed` ? "Updating..." : "Close"}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        <div className="petaar-card">
          {!selectedTicket ? (
            <p>Select a ticket to view details.</p>
          ) : (
            <>
              <h2 style={{ marginTop: 0 }}>{selectedTicket.subject}</h2>
              <p style={{ marginTop: 0, color: "#64748b" }}>
                Ticket ID: {selectedTicket.id} | {selectedTicket.category || "General"} | {roleText(selectedTicket.createdByRole)}
              </p>
              <p style={{ marginTop: 0, color: "#64748b" }}>
                Raised by: {isAdmin ? (selectedTicket.createdByEmail || selectedTicket.createdByUid) : "You"}
                {selectedTicket.bookingId ? ` | Booking: ${selectedTicket.bookingId}` : ""}
              </p>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
                <span>Status: <strong>{selectedTicket.status || "open"}</strong></span>
                <span>Priority: <strong style={{ textTransform: "capitalize" }}>{selectedTicket.priority || "medium"}</strong></span>
              </div>

              <div style={{ border: "1px solid #e2e8f0", borderRadius: 8, maxHeight: 340, overflowY: "auto", padding: 10, marginBottom: 10 }}>
                {loadingMessages ? (
                  <p>Loading conversation...</p>
                ) : messages.length === 0 ? (
                  <p>No messages yet.</p>
                ) : (
                  messages.map((message) => {
                    const isMine = message.senderUid === user?.uid;
                    const roleLabel = message.senderRole === "admin" ? "Petaar Support" : "Customer";
                    return (
                      <div
                        key={message.id}
                        style={{
                          display: "flex",
                          justifyContent: isMine ? "flex-end" : "flex-start",
                          marginBottom: 8,
                        }}
                      >
                        <div
                          style={{
                            maxWidth: "75%",
                            background: isMine ? "#dbeafe" : "#f8fafc",
                            border: "1px solid #dbe3ee",
                            borderRadius: 10,
                            padding: 8,
                          }}
                        >
                          <small style={{ display: "block", color: "#475569", marginBottom: 4 }}>
                            {isMine ? "You" : roleLabel}
                          </small>
                          <div style={{ whiteSpace: "pre-wrap" }}>{message.text}</div>
                          <small style={{ display: "block", color: "#64748b", marginTop: 4 }}>
                            {toMillis(message.createdAt) ? new Date(toMillis(message.createdAt)).toLocaleString() : "Sending..."}
                          </small>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              {isAdmin ? (
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
                  {STATUS_OPTIONS.map((status) => (
                    <button
                      key={`status-${status}`}
                      onClick={() => changeStatus(status)}
                      disabled={updatingStatus || selectedTicket.status === status}
                    >
                      {updatingStatus && selectedTicket.status !== status ? "Updating..." : `Set ${status}`}
                    </button>
                  ))}
                </div>
              ) : selectedTicket.status !== "closed" ? (
                <button onClick={() => changeStatus("closed")} disabled={updatingStatus} style={{ marginBottom: 10 }}>
                  {updatingStatus ? "Closing..." : "Close Ticket"}
                </button>
              ) : null}

              <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
                <textarea
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  placeholder={isAdmin ? "Write support reply..." : "Add more details or reply..."}
                  style={{ flex: 1, minHeight: 70, padding: 10 }}
                />
                <button onClick={sendReply} disabled={sendingReply || !replyText.trim()}>
                  {sendingReply ? "Sending..." : "Send"}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
